import React, {useState, useRef} from "react";
import {iconMapByQType} from "../../utils/SurveyUtils";

function BubblyCustomSelect(props) {

    /*
    Select props:

    - id - must be unique and is mandatory;
    - title - title of select;
    - selectPlaceholder - dropdown placeholder if no item is selected;
    - dataItemsList - list of items the dropdown will display;
        The format of each item must be like:
        {
            uniqueId:
            text:
        }

     - selectedItemIndex - if the select is single selection, this is the prop that will contain the index of the selected item in dataItemsList;
     - handleSelectedItemIndexChange - if the select is single selection, this is the function that will be called when an item is clicked;
        On each call the function call will give as a parameter the current selected index

     - multiple - by default the select will be single selection, if this is set on true, the select will become multiple selection
     - selectedItemsIndexes - if select is multiple selection, this props is an array that contains the indexes of the selected items in  dataItemsList
     - handleSelectedItemsIndexesChange - if select is multiple selection, this is the function that will be called when an item is clicked;
        On each call the function will give as a parameter a list of indexes of the selected items in dataItemsList;
    */

    const selectRef = useRef(null);

    const [initialSelection, setInitialSelection] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(false);
    const [searchBoxInput, setSearchBoxInput] = useState();
    const isMobile = window.innerWidth < 1199;

    const handleClickOutsideSelect = (event) => {
        if (!isMobile) {
            if (event.relatedTarget) {
                if (!selectRef.current.contains(event.relatedTarget)) {
                    setOpenDropdown(false);
                }
            } else {
                setOpenDropdown(false);
            }
        }
    };

    function toggleOpenDropdown() {
        if(typeof props.dataItemsList !== "undefined" && props.dataItemsList.length !== 0) {
            setOpenDropdown(!openDropdown);
            if(!openDropdown) {
                if(props.multiple) {
                    setInitialSelection(props.selectedItemsIndexes);
                } else {
                    setInitialSelection(props.selectedItemIndex);
                }
                selectRef.current.focus();
            }
        } else {
            setOpenDropdown(false);
        }
    }

    const handleMobileModalClose = (event) => {
        event.preventDefault();
        if (props.multiple) {
            props.handleSelectedItemsIndexesChange(initialSelection);
        } else {
            props.handleSelectedItemIndexChange(initialSelection);
        }
        setOpenDropdown(false);
    };

    const handleItemChange = (event, selectedItemIndex) => {
        event.preventDefault();

        if (props.multiple) {
            if (props.selectedItemsIndexes.includes(selectedItemIndex)) {
                let listCopy = JSON.parse(JSON.stringify(props.selectedItemsIndexes));
                listCopy.splice(listCopy.indexOf(selectedItemIndex), 1);
                listCopy = listCopy.sort((a,b) => {return a - b});
                props.handleSelectedItemsIndexesChange(listCopy);
            } else {
                let returnList = [...props.selectedItemsIndexes, selectedItemIndex];
                returnList.sort((a,b) => {return a - b});
                props.handleSelectedItemsIndexesChange(returnList);
            }
        } else {
            if (props.selectedItemIndex === selectedItemIndex) {
                props.handleSelectedItemIndexChange(undefined);
            } else {
                props.handleSelectedItemIndexChange(selectedItemIndex);
            }
            if(!isMobile) {
                setOpenDropdown(false);
            }
        }
    };

    const getMultipleSelectionPlaceholder = (dataItemsList, selectedItemsIndexes) => {
        let placeholder = "";

        for (let i = 0; i < selectedItemsIndexes.length; i++) {
            if (i === 0) {
                placeholder = dataItemsList[selectedItemsIndexes[i]].text;
            } else {
                placeholder = placeholder + ", " + dataItemsList[selectedItemsIndexes[i]].text;
            }
        }

        return placeholder;
    };

    const buildSelectorDropdown = (dataItemsList) => {
        let dropdownItems;
        dropdownItems = dataItemsList.map((dataItem, index) => {
            let hideItem = false;
            if(typeof searchBoxInput !== "undefined" && searchBoxInput !== "") {
                hideItem = !dataItem.text.toLowerCase().includes(searchBoxInput.toLowerCase());
            }
            if (props.multiple) {
                const isSelected = props.selectedItemsIndexes.includes(index);
                return (
                    <label style={{display: hideItem ? "none" : "flex"}} className={"selector__dropdown__list__item " + (isSelected ? " selector__dropdown__list__item--selected" : "")}
                           htmlFor={"dropdown_item_input_id_" + dataItem.uniqueId}
                           key={"dropdown_item_key_" + dataItem.uniqueId}
                           onClick={(event) => {handleItemChange(event, index)}}
                    >
                        <input className="selector__dropdown__list__item__input"
                               type="checkbox"
                               checked={isSelected}
                               readOnly={true}
                               id={"dropdown_item_input_id_" + dataItem.uniqueId}/>
                        <span className="selector__dropdown__list__item__check"></span>
                        <span className="selector__dropdown__list__item__text">{dataItem.text ? dataItem.text : ""}</span>
                    </label>
                );
            } else {
                const isSelected = props.selectedItemIndex === index;
                return (
                    <div style={{display: hideItem ? "none" : "flex"}} className={"selector__dropdown__list__item " + (isSelected ? "selector__dropdown__list__item--selected" : "")}
                         key={"dropdown_item_key_" + dataItem.uniqueId}
                         onClick={(event) => {handleItemChange(event, index)}}
                    >
                        {dataItem.questionType &&
                            (<span className={"selector__dropdown__list__item__icon selector__dropdown__list__item__icon--" + (iconMapByQType[dataItem.questionType] ? iconMapByQType[dataItem.questionType] : "single")}></span>)
                        }
                        <span className="selector__dropdown__list__item__text">{dataItem.text ? dataItem.text : ""}</span>
                    </div>
                );
            }
        });

        const showDropDown = !(typeof props.dataItemsList === "undefined" || props.dataItemsList.length === 0) && openDropdown;

        return (
            <div className={"selector__dropdown " + (showDropDown ? " selector__dropdown--open" : "")}>
                <input className="search_input"
                       type="text"
                       value={searchBoxInput}
                       onChange={handleSearchBoxInputChange}
                       placeholder="Search"/>
                <div className="selector__dropdown__list">
                    {dropdownItems}
                </div>
            </div>
        );
    };

    const handleSearchBoxInputChange = (event) => {
        const {value} = event.target;
        setSearchBoxInput(value);
    };

    const buildSelector = (dataItemsList) => {

        let selectPlaceholder = props.selectPlaceholder ? props.selectPlaceholder : "Choose item";
        let areItemsSelected = false;

        if (props.multiple) {
            areItemsSelected = props.selectedItemsIndexes.length > 0;
            if (areItemsSelected) {
                selectPlaceholder = getMultipleSelectionPlaceholder(props.dataItemsList, props.selectedItemsIndexes);
            }
        } else {
            areItemsSelected = typeof props.selectedItemIndex !== "undefined";
            if (areItemsSelected) {
                selectPlaceholder = dataItemsList[props.selectedItemIndex].text;
            }
        }

        let selectorChoseClass = "selector__chose ";
        selectorChoseClass = selectorChoseClass + (areItemsSelected ? " selector__chose--selection" : (dataItemsList.length > 0 ? "" : " selector__chose--disabled"));

        return (
            <div ref={selectRef}
                 tabIndex={-1}
                 onBlur={handleClickOutsideSelect}
                 className="selector">
                <div className={selectorChoseClass}
                    onClick={toggleOpenDropdown}
                >
                    {selectPlaceholder}
                </div>
                {buildSelectorDropdown(dataItemsList)}
            </div>
        );
    };

    const selector = buildSelector(props.dataItemsList);
    let selectorMobileModalPlaceholder = props.selectPlaceholder ? props.selectPlaceholder : "Choose item";

    return (
        <React.Fragment>
            <div className="selector_container">
                {props.title && (<div className="label">{props.title}</div>)}
                {selector}
            </div>
            {isMobile &&
            (
                <div id={props.id + "_mobile_modal"} className="modal"
                     style={{display: openDropdown ? "block" : "none"}}>
                    <div className="modal-content modal-content--sorting">
                        <span className="close" onClick={handleMobileModalClose}>&times;</span>
                        <div className="modal-content__subTitle">{selectorMobileModalPlaceholder}</div>
                        {buildSelectorDropdown(props.dataItemsList)}
                        <div className="modal-content__actions">
                            <a href="#"
                               onClick={(event) => {event.preventDefault();setOpenDropdown(false);}}
                               className="modal-content__actions__btn modal-content__actions__btn--filledBlue modal-content__actions__btn--fullWidth">Apply</a>
                        </div>
                    </div>
                </div>
            )
            }
        </React.Fragment>
    );
}

export default BubblyCustomSelect;